import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './Tooltip.css'

interface TooltipProps {
  content: string
  children: ReactNode
}

export default function Tooltip({ content, children }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number; maxWidth: number } | null>(null)

  const updatePos = () => {
    const el = wrapperRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    // Prefer above the trigger; clamp within viewport.
    const maxWidth = Math.min(320, Math.max(220, window.innerWidth - 24))
    const left = Math.min(window.innerWidth - 12, Math.max(12, rect.left))
    const top = Math.max(12, rect.top - 10)
    setPos({ top, left, maxWidth })
  }

  useLayoutEffect(() => {
    if (!isVisible) return
    updatePos()
  }, [isVisible])

  useEffect(() => {
    if (!isVisible) return
    const onResize = () => updatePos()
    window.addEventListener('scroll', onResize, true)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('scroll', onResize, true)
      window.removeEventListener('resize', onResize)
    }
  }, [isVisible])

  return (
    <div
      className="tooltip-wrapper"
      ref={wrapperRef}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
    >
      {children}
      {isVisible &&
        pos &&
        createPortal(
          <div
            className="tooltip-content tooltip-content-portal"
            role="tooltip"
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.top,
              transform: 'translateY(-100%)',
              maxWidth: pos.maxWidth,
              zIndex: 99999,
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </div>
  )
}

